import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, DollarSign, FileText, Calendar, Clock, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

export default function EditJobForm() {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: '', category: '', description: '', location: '',
    preferredDate: '', preferredTime: '', duration: '', budget: '', urgency: 'normal'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    'Home & Construction', 'Repair & Maintenance', 'Cleaning & Gardening',
    'Beauty & Wellness', 'Events & Entertainment', 'Education & Training',
    'IT & Technology', 'Business & Professional', 'Transport & Logistics',
    'Food & Hospitality', 'Fashion & Design', 'Health & Medical',
    'Pet Services', 'Security & Safety', 'Photography & Media',
    'Music & Arts', 'Other Services'
  ];

  useEffect(() => {
    const fetchJob = async () => {
      const token = localStorage.getItem('token');
      if (!token) { setError('Please login to edit jobs'); setLoading(false); return; }
      try {
        const res = await fetch(`${API_BASE}/api/jobs/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok) {
          const j = data.job || data;
          setFormData({
            title: j.title || '',
            category: j.category || '',
            description: j.description || '',
            location: j.location || '',
            preferredDate: j.preferredDate ? j.preferredDate.split('T')[0] : '',
            preferredTime: j.preferredTime || '',
            duration: j.duration || '',
            budget: j.budget || '',
            urgency: j.urgency || 'normal'
          });
        } else { setError('Failed to load job'); }
      } catch (err) { setError('Failed to load job'); }
      finally { setLoading(false); }
    };
    fetchJob();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...formData, budget: Number(formData.budget) })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => { window.location.href = '/dashboard'; }, 2000);
      } else {
        setError(data.message || 'Failed to update job');
        setSaving(false);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 py-8 px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-3xl mx-auto">
        <button onClick={() => window.location.href = '/dashboard'}
          className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors">
          <ArrowLeft className="w-5 h-5" /><span className="font-medium">Back to Dashboard</span>
        </button>

        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-2xl p-5 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div><p className="font-bold text-green-900 dark:text-green-100">Job Updated!</p><p className="text-sm text-green-700 dark:text-green-300">Redirecting to dashboard...</p></div>
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl p-5 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl shadow-lg transform rotate-12 mb-4">
              <FileText className="w-8 h-8 text-white -rotate-12" />
            </div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-2">Edit Job</h1>
            <p className="text-gray-600 dark:text-gray-400">Update your job details</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Job Title *</label>
              <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required
                placeholder="e.g., Need a plumber for bathroom repair"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Category *</label>
              <select value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} required
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 outline-none text-gray-900 dark:text-gray-100">
                <option value="">Select category</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description *</label>
              <div className="relative">
                <FileText className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} required rows={5}
                  placeholder="Describe what you need done..."
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 resize-none" />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Location *</label>
              <div className="relative">
                <MapPin className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                <input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} required
                  placeholder="e.g., Colombo 05"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Preferred Date</label>
                <div className="relative">
                  <Calendar className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                  <input type="date" value={formData.preferredDate} onChange={e => setFormData({...formData, preferredDate: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 outline-none text-gray-900 dark:text-gray-100" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Preferred Time</label>
                <div className="relative">
                  <Clock className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                  <input type="time" value={formData.preferredTime} onChange={e => setFormData({...formData, preferredTime: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 outline-none text-gray-900 dark:text-gray-100" />
                </div>
              </div>
            </div>

            {/* Budget & Urgency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Budget (LKR)</label>
                <div className="relative">
                  <DollarSign className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                  <input type="number" value={formData.budget} onChange={e => setFormData({...formData, budget: e.target.value})} min="0"
                    placeholder="Your budget"
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Urgency *</label>
                <select value={formData.urgency} onChange={e => setFormData({...formData, urgency: e.target.value})} required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 outline-none text-gray-900 dark:text-gray-100">
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                  <option value="asap">ASAP</option>
                </select>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" disabled={saving || success}
              className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-yellow-400/30 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2">
              {saving && <Loader className="w-5 h-5 animate-spin" />}
              {saving ? 'Updating...' : success ? '✅ Updated!' : 'Update Job'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}